import { Tooltip } from 'antd';
import { Zone } from '../../../@types/projects/types';
import { useTheme } from '../../../providers/theme';
import { getColors } from '../../utils/getColors';
import { isInvalidFraction, isInvalidNumber } from '../utils';
import { AnalyzeType } from '../../../@types/samples/types';

type WaterOilKeys =
  | `key`
  | `numberOfPoints`
  | `fu${number}Height`
  | `fu${number}Sw`
  | `fu${number}Krw`
  | `fu${number}Krow`
  | `fu${number}Pcow`
  | `fu${number}Krw (Scaled)`
  | `fu${number}Krow (Scaled)`;

export type WaterOilTablePropData = {
  [key in WaterOilKeys]: string;
};

export function getWaterOilData(
  zones: Zone[], 
  analysis_type: AnalyzeType,
  showScaledKr: boolean = false
) {
  const maxPoints = Math.max(
    ...zones.map(
      zone =>
        zone.zone_curves?.liao_curves?.sw.length ||
        zone.zone_curves?.lambda_curves?.sw.length ||
        0
    )
  );

  return Array.from({ length: maxPoints }, (_, pointIndex) => {
    const result: any = {
      key: (pointIndex + 1).toString(),
      numberOfPoints: pointIndex.toString()
    };

    zones.forEach((zone, zoneIndex) => {
      const name = `fu${zoneIndex + 1}`;
      const zoneCurves =
        analysis_type === AnalyzeType.LIAO
          ? zone?.zone_curves?.liao_curves
          : zone?.zone_curves?.lambda_curves;
      const zoneValues =
        analysis_type === AnalyzeType.LIAO
          ? zone?.zone_values?.liao_values
          : zone?.zone_values?.lambda_values;
      const firstKrog = zoneCurves?.krog[0];

      if (zoneCurves && zoneCurves.sw[pointIndex] !== undefined) {
        const krwValue = zoneCurves.krw[pointIndex]?.toFixed(4);
        const krowValue = zoneCurves.krow[pointIndex]?.toFixed(4);
        const swValue = zoneCurves.sw[pointIndex]?.toFixed(4);
        const heightValue = zoneCurves.height[pointIndex]?.toFixed(4);
        const pcowValue = zoneCurves.pcow[pointIndex]?.toFixed(4);
        const krwscaled = zoneCurves.krw_scaled[pointIndex]?.toFixed(4);
        const krowscaled = zoneCurves.krow_scaled[pointIndex]?.toFixed(4);

        result[`${name}Height`] = heightValue;
        result[`${name}Sw`] = swValue;
        result[`${name}Pcow`] = pcowValue;
        result[`${name}Krw (Scaled)`] = krwscaled;
        result[`${name}Krow (Scaled)`] = krowscaled;

        // Change krw and krow values based on showScaledKr if showScaledKr is true
        result[`${name}Krw`] = showScaledKr ? krwscaled : krwValue;
        result[`${name}Krow`] = showScaledKr ? krowscaled : krowValue;

        if (pointIndex === 0) {
          if (krowValue !== undefined && firstKrog !== undefined) {
            result[`${name}KrowColor`] =
              parseFloat(krowValue) === firstKrog ? '#17B169' : 'red';
          }
          if (krwValue !== undefined) {
            result[`${name}KrwColor`] =
              parseFloat(krwValue) === 0.0 ? '#17B169' : 'red';
          }
          if (swValue !== undefined) {
            result[`${name}SwColor`] =
              parseFloat(swValue) === zoneValues?.swir ? '#17B169' : 'red';
          }
        }

        if (pointIndex === maxPoints - 1) {
          if (krowValue !== undefined) {
            result[`${name}KrowColor`] =
              parseFloat(krowValue) === 0.0 ? '#17B169' : 'red';
          }
        }

        if (isInvalidFraction(swValue)) result[`${name}swColor`] = 'red';
        if (isInvalidFraction(krwValue)) result[`${name}KrwColor`] = 'red';
        if (isInvalidFraction(krowValue)) result[`${name}KrowColor`] = 'red';
        if (isInvalidNumber(heightValue)) result[`${name}HeightColor`] = 'red';
        if (isInvalidNumber(pcowValue)) result[`${name}PcowColor`] = 'red';
      }
    });

    return result;
  });
}

export const GenerateColumns = (
  zones: Zone[], 
  showScaledKr: boolean = false,
  depthUnit: "m" | "ft",
  pressureUnit: "psi" | "bar"
) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);

  const baseColumns = [
    {
      title: 'Number of Points',
      dataIndex: 'numberOfPoints',
      key: 'numberOfPoints',
      width: 100
    }
  ];

  const zoneColumns = Array.from({ length: zones.length }, (_, index) => {
    const zoneNumber = index + 1;
    const color = zones[index].color || '#fff';

    // Colunas padrão
    const children = [
      {
        title: `Height (${depthUnit})`,
        dataIndex: `fu${zoneNumber}Height`,
        key: `fu${zoneNumber}Height`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}HeightColor`];
          return (
            <Tooltip title={color === 'red' ? 'Height must be > 0' : ''}>
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      },
      {
        title: 'Sw (frac)',
        dataIndex: `fu${zoneNumber}Sw`,
        key: `fu${zoneNumber}Sw`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}SwColor`];
          return (
            <Tooltip
              title={
                color === 'red' &&
                (record.key === '1'
                  ? 'Sw must be = Swir'
                  : 'Sw must > 0 and < 1')
              }
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      },
      {
        title: showScaledKr ? 'Krw scaled (frac)' : 'Krw (frac)',
        dataIndex: `fu${zoneNumber}Krw`,
        key: `fu${zoneNumber}Krw`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}KrwColor`];
          return (
            <Tooltip
              title={
                color === 'red' &&
                (record.key === '1'
                  ? 'Krw must be = 0'
                  : 'Krw must be > 0 and < 1')
              }
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      },
      {
        title: showScaledKr ? 'Krow scaled (frac)' : 'Krow (frac)',
        dataIndex: `fu${zoneNumber}Krow`,
        key: `fu${zoneNumber}Krow`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}KrowColor`] || colors.text;
          return (
            <Tooltip
              title={
                color === 'red' &&
                (record.key === '1'
                  ? 'Krow must be = Krog'
                  : 'Krow must be > 0 and < 1')
              }
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      },
      {
        title: `Pcow (${pressureUnit})`,
        dataIndex: `fu${zoneNumber}Pcow`,
        key: `fu${zoneNumber}Pcow`,
        width: 80,
        render: (text: string, record: any) => {
          const color = record[`fu${zoneNumber}PcowColor`];
          return (
            <Tooltip
              title={color === 'red' ? 'Pcow must be > 0 and < 1' : ''}
            >
              <span style={{ color }}>{text}</span>
            </Tooltip>
          );
        }
      }
    ];

    // if want to show extra columns (optional)
    // if (showScaledKr) {
    //   children.push(
    //     {
    //       title: 'Krw (Scaled)',
    //       dataIndex: `fu${zoneNumber}Krw (Scaled)`,
    //       key: `fu${zoneNumber}Krw (Scaled)`,
    //       width: 80,
    //       render: (text: string) => (
    //         <Tooltip>
    //           <span>{text}</span>
    //         </Tooltip>
    //       )
    //     },
    //     {
    //       title: 'Krow (Scaled)',
    //       dataIndex: `fu${zoneNumber}Krow (Scaled)`,
    //       key: `fu${zoneNumber}Krow (Scaled)`,
    //       width: 80,
    //       render: (text: string) => (
    //         <Tooltip>
    //           <span>{text}</span>
    //         </Tooltip>
    //       )
    //     }
    //   );
    // }

    return {
      title: <span style={{ color }}>{`RT${zoneNumber}`}</span>,
      children
    };
  });

  return [...baseColumns, ...zoneColumns];
};
